import type { ChangeEvent } from 'react';
import { PERSPECTIVE_ORDER } from '../config/kpi';
import { runtimeConfig } from '../config/runtime';
import type { DashboardFilters, KpiStatus } from '../types/bsc';

interface FilterDrawerProps {
  open: boolean;
  filters: DashboardFilters;
  availableStatuses: KpiStatus[];
  showMoreDetails: boolean;
  showBreakdownTable: boolean;
  refreshing: boolean;
  onClose: () => void;
  onFiltersChange: (filters: DashboardFilters) => void;
  onShowMoreDetailsChange: (value: boolean) => void;
  onShowBreakdownTableChange: (value: boolean) => void;
  onReload: () => void;
}

function toggleValue<T extends string>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export function FilterDrawer({
  open,
  filters,
  availableStatuses,
  showMoreDetails,
  showBreakdownTable,
  refreshing,
  onClose,
  onFiltersChange,
  onShowMoreDetailsChange,
  onShowBreakdownTableChange,
  onReload,
}: FilterDrawerProps) {
  const resetFilters = () =>
    onFiltersChange({
      perspectives: [...PERSPECTIVE_ORDER],
      statuses: [...availableStatuses],
      query: '',
    });

  return (
    <>
      <button
        type="button"
        className={`drawer-backdrop${open ? ' drawer-backdrop--open' : ''}`}
        aria-label="Chiudi controlli"
        onClick={onClose}
      />
      <aside className={`filter-drawer${open ? ' filter-drawer--open' : ''}`} aria-hidden={!open}>
        <div className="drawer-header">
          <div>
            <span className="eyebrow">Dashboard</span>
            <h2>Controlli</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Chiudi" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="drawer-content">
          <section className="control-section">
            <h3>Vista</h3>
            <label className="switch-row">
              <span>
                <strong>Mostra più dettagli</strong>
                <small>Responsabile, fonte, formula, target e soglie</small>
              </span>
              <input
                type="checkbox"
                checked={showMoreDetails}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onShowMoreDetailsChange(event.target.checked)
                }
              />
            </label>
            <label className="switch-row">
              <span>
                <strong>Mostra tabelle di dettaglio</strong>
                <small>Serie multilinea per reparto, cliente, commessa o BU</small>
              </span>
              <input
                type="checkbox"
                checked={showBreakdownTable}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onShowBreakdownTableChange(event.target.checked)
                }
              />
            </label>
          </section>

          <section className="control-section">
            <h3>Cerca KPI</h3>
            <label className="field-label" htmlFor="kpi-search">
              Codice o nome
            </label>
            <input
              id="kpi-search"
              className="text-input"
              type="search"
              value={filters.query}
              placeholder="es. FIN-02 oppure EBITDA"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onFiltersChange({ ...filters, query: event.target.value })
              }
            />
          </section>

          <section className="control-section">
            <h3>Prospettiva</h3>
            <div className="checkbox-list">
              {PERSPECTIVE_ORDER.map((perspective) => (
                <label key={perspective} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={filters.perspectives.includes(perspective)}
                    onChange={() =>
                      onFiltersChange({
                        ...filters,
                        perspectives: toggleValue(filters.perspectives, perspective),
                      })
                    }
                  />
                  <span>{perspective}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="control-section">
            <h3>Stato</h3>
            <div className="checkbox-list">
              {availableStatuses.map((status) => (
                <label key={status} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={filters.statuses.includes(status)}
                    onChange={() =>
                      onFiltersChange({
                        ...filters,
                        statuses: toggleValue(filters.statuses, status),
                      })
                    }
                  />
                  <span>{status}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="control-section data-source-box">
            <h3>Sorgente dati</h3>
            <dl>
              <div>
                <dt>Modalità</dt>
                <dd>{runtimeConfig.dataMode === 's3' ? 'S3 privato' : 'File locali di sviluppo'}</dd>
              </div>
              <div>
                <dt>Registry</dt>
                <dd title={runtimeConfig.registryKey}>{runtimeConfig.registryKey}</dd>
              </div>
              <div>
                <dt>Valori</dt>
                <dd title={runtimeConfig.valuesKey}>{runtimeConfig.valuesKey}</dd>
              </div>
            </dl>
          </section>
        </div>

        <div className="drawer-footer">
          <button className="button button--secondary" type="button" onClick={resetFilters}>
            Azzera filtri
          </button>
          <button
            className="button button--primary"
            type="button"
            onClick={onReload}
            disabled={refreshing}
          >
            {refreshing ? 'Aggiornamento…' : 'Ricarica Excel'}
          </button>
        </div>
      </aside>
    </>
  );
}
